import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import { IBillingEntityJSON } from 'features/Directory'
import { IPurchaseOrderItemSelection } from 'features/PurchaseOrders'
import { isEmpty } from 'lodash-es'
import { useApi } from 'service/useApi'
import { useRootStore } from 'service/useRootStore'
import { useI18n } from 'utils/localisation'
import { logError } from 'utils/logger'

import { BookingInterface } from '../BookingModel'

export type CreateBookingPayload = Partial<BookingInterface> & {
  option: number
  bookedByFreightHub: boolean
  templateId?: string
  purchaseOrderItemSelections?: IPurchaseOrderItemSelection[]
}

export type BookQuoteSheetResponse = {
  shipmentId: string
  bookingRequestId?: string
}

export const useTransportPlanSchemaMutation = (reportError = logError) => {
  const { transportPlanService } = useRootStore()
  const { translate } = useI18n()

  return useMutation({
    mutationFn: async (bookedResponse: BookQuoteSheetResponse) => {
      try {
        await transportPlanService.get(bookedResponse.shipmentId)
        return bookedResponse
      } catch (error) {
        if (error instanceof Error && /unsupported type mode combination/i.test(error.message)) {
          return bookedResponse
        }
        throw new Error(translate('error_popup-server_error_text'))
      }
    },
    retry: (retryCount, error) => {
      if (retryCount < 3) {
        return true
      }
      if (error instanceof Error) {
        reportError(new Error('Could not fetch transport scheme'))
      }
      return false
    },
    retryDelay: 1000,
  })
}

export const useBookQuoteSheetMutation = (quoteSheetID: string) => {
  const { API } = useRootStore()

  return useMutation({
    mutationFn: async (payload: CreateBookingPayload) => {
      const result = await API.quoteSheets.book(quoteSheetID, payload)

      return result
    },
  })
}

export const useBillingLegalEntity = (
  billingLegalEntityID?: string
): UseQueryResult<IBillingEntityJSON | undefined, unknown> => {
  const { network } = useApi()

  return useQuery({
    queryFn: async () => {
      const billingEntitiesResponse = await network.billingEntities.getBillingEntities()
      return billingEntitiesResponse.find(({ _id }) => _id === billingLegalEntityID)
    },
    queryKey: ['billing-entities', billingLegalEntityID],
    enabled: !isEmpty(billingLegalEntityID),
  })
}
